import { useQuery } from 'react-query';
import { axiosClient } from '@/services/utilities';

export interface PublisherUsername {
  username: string;
  doc_count: number;
}

export interface PublisherUsernamesResponse {
  publishers: PublisherUsername[];
  after_key?: string;
}

export interface UsePublisherUsernamesParams {
  search?: string;
  size?: number;
  after_key?: string;
}

export const usePublisherUsernames = (params?: UsePublisherUsernamesParams) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<PublisherUsernamesResponse>({
    queryKey: ['publisher-usernames', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      
      if (params?.search) {
        searchParams.append('search', params.search);
      }
      
      if (params?.size) {
        searchParams.append('size', String(params.size));
      } else {
        searchParams.append('size', '100');
      }
      
      if (params?.after_key) {
        searchParams.append('after_key', params.after_key);
      }

      const url = `/publishers/usernames?${searchParams.toString()}`;

      try {
        const response = await axiosClient.get(url, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching publishers:', error);
        throw error;
      }
    },
    enabled: true,
    retry: 3,
    retryDelay: 1000,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  return {
    publishers: data?.publishers || [],
    afterKey: data?.after_key,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export interface PublisherApi {
  name: string;
  version: string;
  doc_count: number;
}

export interface PublisherApisResponse {
  apis: PublisherApi[];
  total: number;
}

export interface UsePublisherApisParams {
  publisherUsername: string;
  search?: string;
  size?: number;
}

export const usePublisherApis = (params: UsePublisherApisParams) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<PublisherApisResponse>({
    queryKey: ['publisher-apis', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      
      if (params.search) {
        searchParams.append('search', params.search);
      }
      
      if (params.size) {
        searchParams.append('size', String(params.size));
      } else {
        searchParams.append('size', '100');
      }

      const response = await axiosClient.get(
        `/publishers/${params.publisherUsername}/apis?${searchParams.toString()}`
      );

      return response.data;
    },
    enabled: !!params.publisherUsername,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    apis: data?.apis || [],
    total: data?.total || 0,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export interface Subscriber {
  username: string;
  fullname: string;
  email: string;
  doc_count: number;
}

export interface SubscribersResponse {
  subscribers: Subscriber[];
  after_key?: string;
}

export interface UseSubscribersParams {
  publisherUsername?: string;
  apiName?: string;
  apiVersion?: string;
  search?: string;
  size?: number;
  after_key?: string;
  enabled?: boolean; // Add enabled parameter
}

export const useSubscribers = (params?: UseSubscribersParams) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<SubscribersResponse>({
    queryKey: ['subscribers', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      
      if (params?.publisherUsername) {
        searchParams.append('publisherUsername', params.publisherUsername);
      }
      
      if (params?.apiName) {
        searchParams.append('apiName', params.apiName);
      }
      
      if (params?.apiVersion) {
        searchParams.append('apiVersion', params.apiVersion);
      }
      
      if (params?.search) {
        searchParams.append('search', params.search);
      }
      
      if (params?.size) {
        searchParams.append('size', String(params.size));
      } else {
        searchParams.append('size', '10');
      }
      
      if (params?.after_key) {
        searchParams.append('after_key', params.after_key);
      }

      try {
        const response = await axiosClient.get(
          `/subscribers?${searchParams.toString()}`
        );

        return response.data;
      } catch (error: any) {
        console.error('Error fetching subscribers:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch subscribers';
        throw new Error(errorMessage);
      }
    },
    // Allow enabled to be controlled from params
    enabled: params?.enabled !== undefined ? params.enabled : true,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    subscribers: data?.subscribers || [],
    afterKey: data?.after_key,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};